import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useReauthModalState, setCachedReauthToken } from '../../hooks/useReauth';
import { resolveApiBaseUrl } from '../../utils/apiClient';

const API_BASE_URL = resolveApiBaseUrl();

export default function ReauthModal() {
  const { session } = useAuth();
  const { open, close } = useReauthModalState();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/reauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {}),
        },
        body: JSON.stringify({ password, totpCode: code }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(err.message || 'Re-authentication failed');
      }
      const { reauthToken } = await res.json() as { reauthToken: string };
      setCachedReauthToken(reauthToken);
      setPassword('');
      setCode('');
      close(reauthToken);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Re-authentication failed');
    } finally {
      setBusy(false);
    }
  }

  function onCancel() {
    setPassword('');
    setCode('');
    setError('');
    close(null);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <form
        onSubmit={onSubmit}
        className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full"
      >
        <h2 className="text-lg font-bold text-[var(--pv-color-primary)] mb-2">Confirm your identity</h2>
        <p className="text-sm text-gray-600 mb-4">
          This action requires re-authentication. Enter your password and 6-digit TOTP code.
        </p>
        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <i className="ri-error-warning-line mr-2"></i>{error}
          </div>
        )}
        <input
          type="password"
          placeholder="Password"
          autoFocus
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border border-gray-200 rounded-xl p-3 mb-3"
        />
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="w-full border border-gray-200 rounded-xl p-3 mb-4 tracking-widest text-center text-lg"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || password.length === 0 || code.length !== 6}
            className="px-4 py-2 rounded-xl bg-[#004D40] text-white font-medium hover:bg-[#003930] disabled:opacity-50"
          >
            {busy ? 'Verifying…' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
